
import { GameState } from '../GameContextTypes';
import { 
  getCareerLevelMultiplier, 
  calculateBaseKpiImprovements, 
  applyRoleSpecificKpiBonuses 
} from './utils/kpiUpdates';
import { 
  countSkillsByCategory, 
  applySkillCategoryEffects, 
  applySpecificSkillEffects 
} from './utils/skillEffects';
import { applyRandomEvents } from './utils/randomEvents';
import { calculateYearlyExperienceGain } from './utils/experienceGain';

export const advanceYear = (state: GameState): GameState => {
  const yearsPassed = state.yearsPassed + 1;
  
  // Update KPIs with balanced progression
  let updatedKpis = { ...state.kpis };
  
  // Get natural KPI improvement factors
  const experienceFactor = Math.min(1, state.experience / 500); // Max boost at 500 experience
  const careerMultiplier = getCareerLevelMultiplier(state.careerLevel);
  
  // Calculate base KPI improvements
  const baseImprovements = calculateBaseKpiImprovements(state, careerMultiplier);
  
  // Apply base improvements with diminishing returns and experience factors
  updatedKpis.uptime = Math.min(100, updatedKpis.uptime + 
    (baseImprovements.uptimeImprovement * (1 + experienceFactor * 0.5)));
  updatedKpis.energyCost = Math.max(0.03, updatedKpis.energyCost - 
    (baseImprovements.energyCostReduction * (1 + experienceFactor * 0.5)));
  updatedKpis.profitMargin = Math.min(40, updatedKpis.profitMargin + 
    (baseImprovements.profitMarginImprovement * (1 + experienceFactor * 0.5)));
  updatedKpis.efficiency = Math.min(95, updatedKpis.efficiency + 
    (baseImprovements.efficiencyImprovement * (1 + experienceFactor * 0.5)));
  
  // Apply role-specific KPI bonuses
  if (state.selectedRole) {
    updatedKpis = applyRoleSpecificKpiBonuses(updatedKpis, state.selectedRole.id, careerMultiplier);
  }
  
  // Count skills by category for scaling effects
  const skillCounts = countSkillsByCategory(state.skills);
  
  // Apply skill category and specific skill effects
  for (const skill of state.skills) {
    // Apply category-based effects
    updatedKpis = applySkillCategoryEffects(updatedKpis, skill, skillCounts, careerMultiplier);
    
    // Apply special effects for specific skills
    updatedKpis = applySpecificSkillEffects(updatedKpis, skill.id, careerMultiplier);
  }
  
  // Apply random events
  updatedKpis = applyRandomEvents(updatedKpis);
  
  // Round values to prevent floating point issues
  updatedKpis.uptime = Number(updatedKpis.uptime.toFixed(2));
  updatedKpis.energyCost = Number(updatedKpis.energyCost.toFixed(4));
  updatedKpis.profitMargin = Number(updatedKpis.profitMargin.toFixed(2));
  updatedKpis.efficiency = Number(updatedKpis.efficiency.toFixed(2));
  
  // Calculate experience gain
  const experienceGain = calculateYearlyExperienceGain(state.careerLevel, yearsPassed, updatedKpis);
  
  return {
    ...state,
    yearsPassed,
    kpis: updatedKpis,
    experience: state.experience + experienceGain
  };
};
